import type { ThemeConfig } from 'antd';
import { breakpoints, colors, interactiveBorder, layout, radius, typography } from './designTokens';

/**
 * antd `ConfigProvider` theme, built from `designTokens.ts` (ADR-0007).
 * Mirrors HMS `src/styles/themeConfig.js` so both products theme identically.
 * No hex literals here — every value comes from the token module.
 */
/**
 * **antd's breakpoints, realigned to this project's.**
 *
 * antd ships `lg: 992` and `xl: 1200`; `designTokens.breakpoints` — which is
 * what `tailwind.config.js` reads — uses `lg: 1024` and `xl: 1280`. They agreed
 * only at `md`. `use-media-query.ts` claimed for months that the two "can never
 * disagree", and that sentence had a measured cost: the 44px touch floor was
 * dropped at 992 instead of 1024, so **992–1023 — tablets, the exact band the
 * floor exists for — rendered 32px controls** (QA F-1).
 *
 * Aligning the tokens, rather than deleting the claim, is what makes the claim
 * true: antd's own responsive internals and any direct `Grid.useBreakpoint()`
 * call now land on the same numbers as a `lg:` class.
 *
 * ## Every `Min`/`Max` is listed, and that is not redundancy
 *
 * **antd does NOT derive `screen*Min`/`screen*Max` from the five base tokens.**
 * Setting `screenXXL: 1536` while antd's `screenXXLMin` stayed at its default
 * 1600 made `validateBreakpoints` throw `screenXXLMin<=screenXXL fails`, which
 * is a **render-time** error inside `useResponsiveObserver` — so the whole app
 * white-screened behind the router's error boundary while `tsc` and `vite
 * build` both passed clean. Found by loading the page, not by reasoning about
 * it. Every value below is therefore stated explicitly, and the ladder must
 * stay internally consistent: each `Max` is the next `Min` minus one.
 *
 * `screenXS` keeps antd's 480 because Tailwind's smallest is `sm`, so there is
 * no project number to align it to — but its `Max` still moves to 639 to meet
 * `screenSMMin`.
 *
 * ## This is necessary but NOT sufficient
 *
 * `Grid.useBreakpoint()` reads these tokens from the nearest `ConfigProvider`.
 * `app/providers.tsx` picks which theme to pass by calling a breakpoint hook
 * **above** that provider, where no override is in scope — so the tokens alone
 * cannot move the theme switch, and the same hook would answer differently
 * depending on where it was called. That is why `use-media-query.ts` now asks
 * `matchMedia` directly instead of going through antd.
 */
const SCREEN_TOKENS = {
  screenXSMax: breakpoints.sm - 1,
  screenSM: breakpoints.sm,
  screenSMMin: breakpoints.sm,
  screenSMMax: breakpoints.md - 1,
  screenMD: breakpoints.md,
  screenMDMin: breakpoints.md,
  screenMDMax: breakpoints.lg - 1,
  screenLG: breakpoints.lg,
  screenLGMin: breakpoints.lg,
  screenLGMax: breakpoints.xl - 1,
  screenXL: breakpoints.xl,
  screenXLMin: breakpoints.xl,
  screenXLMax: breakpoints['2xl'] - 1,
  screenXXL: breakpoints['2xl'],
  screenXXLMin: breakpoints['2xl'],
} as const;

/** antd's `Segmented` inset between the track and each segment, in px. */
const SEGMENTED_TRACK_PADDING = 2;

export const lightTheme: ThemeConfig = {
  token: {
    ...SCREEN_TOKENS,
    colorPrimary: colors.primary.DEFAULT,
    colorInfo: colors.info.light,
    colorSuccess: colors.success.light,
    colorError: colors.error.light,
    colorWarning: colors.warning.light,
    colorTextBase: colors.text.light.base,
    colorTextSecondary: colors.text.light.muted,
    colorTextTertiary: colors.text.light.placeholder,
    colorBorder: colors.border.light,
    colorBorderSecondary: colors.border.strong,
    colorBgBase: colors.surface.light.container,
    colorBgLayout: colors.surface.light.base,
    colorBgContainer: colors.surface.light.container,
    colorBgElevated: colors.surface.light.elevated,
    colorLink: colors.primary.DEFAULT,
    colorLinkHover: colors.primary.hover,
    borderRadius: radius.md,
    fontFamily: typography.fontFamily,
    fontSize: typography.fontSize.sm,
    /**
     * antd's `size="large"` is 40px, which is under the WCAG 2.5.5 floor.
     * Raising it here — from the token, once — makes every large Input,
     * Select and Button a compliant touch target, instead of every form
     * pinning `min-h-touch-target` on every control and one being forgotten.
     * `size="large"` is therefore the house style for anything a gloved hand
     * has to hit on the shop floor.
     */
    controlHeightLG: layout.touchTarget,
  },
  components: {
    Layout: {
      headerBg: colors.surface.light.container,
      headerColor: colors.navy.DEFAULT,
      siderBg: colors.navy.DEFAULT,
      bodyBg: colors.surface.light.base,
      /*
       * The shell header is the identity row (HMS `h-16`); the navigation row
       * below it is a plain sticky `<nav>`, not a second `Layout.Header`, so
       * this token describes exactly one bar. Horizontal padding is 0 here
       * because `TopBar` sets the responsive `px-4 sm:px-6 lg:px-8` gutter
       * itself — antd's fixed padding would fight it at every breakpoint.
       */
      headerHeight: layout.appHeaderIdentityHeight,
      headerPadding: '0',
    },
    Menu: {
      itemSelectedBg: colors.primary.subtle,
      itemSelectedColor: colors.primary.DEFAULT,
      itemHoverColor: colors.primary.hover,
      /*
       * The horizontal menu is the shell's second header bar and must sit
       * inside it exactly. antd's default (46px) overflows the bar and pushes
       * the label out below the border. Setting the token — rather than
       * fighting antd's internals with `!h-...` overrides — keeps the item
       * box, its hover/selected background and its underline all agreeing on
       * one height, and makes each item a full-height 44px target.
       */
      horizontalLineHeight: `${layout.appHeaderNavHeight}px`,
      horizontalItemSelectedColor: colors.primary.DEFAULT,
      // The sider runs the navy gradient, so its Menu is the dark variant.
      darkItemBg: 'transparent',
      darkPopupBg: colors.navy.DEFAULT,
      darkItemColor: colors.text.light.onDark,
      darkItemSelectedBg: colors.primary.DEFAULT,
      darkItemSelectedColor: colors.surface.light.container,
      darkItemHoverBg: colors.navy[500],
      darkItemHoverColor: colors.surface.light.container,
    },
    /**
     * The one place the segmented ("pill toggle") control is skinned — read
     * through the `SegmentedControl` atom (`components/ui/SegmentedControl`),
     * which mirrors HMS's `atoms/SegmentedControl.jsx`.
     *
     * A soft surface track with a raised white selected thumb: `ink-muted` on
     * the track measures 5.77 (AA). `primary` (#f15b40) on the white thumb
     * measures about 3.3:1, which is below AA 4.5:1 for this text size. The
     * manager chose on 2026-10-08 to keep the brand colour (FU-UI-1, closed);
     * an earlier version of this comment claimed 5.93, which was wrong.
     * Selection is never colour alone; antd renders the thumb as a raised
     * surface, and the control is a radiogroup so the state is announced.
     */
    Segmented: {
      /*
       * **The segment is the target, not the track.** antd sizes a large
       * segment as `controlHeightLG - 2 * trackPadding`, so inheriting the
       * global 44 made the *control* 44 and every segment in it 40 — under
       * the 44px floor `responsive-design.md` binds below `lg` (QA 021 O-1,
       * 011 O-1). Raising the control by the track inset, here and only for
       * this component, makes each segment exactly the touch-target token.
       * `pointerTheme` restores the house density from `lg` up.
       */
      controlHeightLG: layout.touchTarget + SEGMENTED_TRACK_PADDING * 2,
      trackBg: colors.surface.light.base,
      trackPadding: SEGMENTED_TRACK_PADDING,
      itemColor: colors.text.light.muted,
      itemHoverColor: colors.primary.hover,
      itemHoverBg: colors.primary.subtle,
      itemSelectedBg: colors.surface.light.container,
      itemSelectedColor: colors.primary.DEFAULT,
      borderRadius: radius.md,
      borderRadiusSM: radius.sm,
      borderRadiusLG: radius.md,
    },
    Tag: {
      // The workspace identity pill; its tint and dot come from Tailwind
      // token classes in `workspace-appearance.ts`.
      borderRadiusSM: radius.sm,
      defaultColor: colors.text.light.base,
    },
    /**
     * The shared button design, ported from HMS `styles/hmsControls.css`
     * (`.hms-btn`). It lives here rather than in a stylesheet because
     * ADR-0007 keeps hex out of CSS, and because HMS's literal values are
     * their brand blue — the *structure* of their design ports, the palette
     * is ours. Holding it in the theme also reaches the files that still
     * call antd's `Button` directly, not only `components/ui/Button.tsx`.
     *
     * `borderRadius` already agreed: `radius.sm` is 6, the same 6px
     * `.hms-btn` pins. The default border is a wash too — HMS's `#cbd5e1`
     * and our `border.light` differ by four points per channel — so no new
     * token was minted for it.
     */
    Button: {
      colorPrimary: colors.primary.DEFAULT,
      colorPrimaryHover: colors.primary.hover,
      colorPrimaryActive: colors.primary.active,
      colorTextLightSolid: colors.surface.light.container,
      colorBgContainer: colors.surface.light.container,
      colorBgTextHover: colors.primary.subtle,
      colorBgTextActive: colors.border.strong,
      borderRadius: radius.sm,
      /*
       * `size="large"` is this codebase's house default (it carries the touch
       * floor), and antd resolves a large control's corner from
       * `borderRadiusLG` — which derives from the global 8 and renders 10.
       * Pinning it to the same 6 keeps `.hms-btn`'s corner at every size
       * instead of only on the middle one.
       */
      borderRadiusLG: radius.sm,
      defaultShadow: 'none',
      primaryShadow: 'none',
      // `.hms-btn.ant-btn { font-weight: 400 }` — antd's own default, pinned
      // in both products so a heavier base font can never leak into buttons.
      fontWeight: 400,
      /*
       * `.hms-btn.ant-btn-default:hover` — HMS tints a default button with
       * its primary on hover instead of darkening the border. One change:
       * they use a pale tint for the hover *border* (`#bae6fd`); we use the
       * accent itself, because `interactiveBorder` requires a hovered
       * control's edge to clear 3:1 and `primary.DEFAULT` is the token
       * documented at 3.33. The fill stays a wash, as theirs is.
       */
      defaultHoverColor: colors.primary.DEFAULT,
      defaultHoverBorderColor: interactiveBorder.accent,
      defaultHoverBg: colors.primary.subtle,
    },
    Input: {
      colorBgContainer: colors.surface.light.container,
      colorBorder: colors.border.light,
      colorText: colors.text.light.base,
      colorTextPlaceholder: colors.text.light.placeholder,
      borderRadius: radius.sm,
      // See `Button.borderRadiusLG` — same reason, same 6px corner.
      borderRadiusLG: radius.sm,
    },
    /** The filter row sets Select beside Input; both corners must agree. */
    Select: {
      borderRadius: radius.sm,
      borderRadiusLG: radius.sm,
    },
    /**
     * HMS `.hms-datepicker` / `.hms-timepicker`: the same 6px corner as the
     * button and the input, so a filter row of mixed controls lines up.
     * Their hover/focus border rule is antd's default behaviour once
     * `colorPrimary` is set, so it needs no token here.
     */
    DatePicker: {
      borderRadius: radius.sm,
      borderRadiusLG: radius.sm,
    },
    Card: {
      colorBgContainer: colors.surface.light.container,
      boxShadow: '0 4px 12px rgba(1, 40, 63, 0.08)',
      borderRadius: radius.lg,
    },
    Table: {
      headerBg: colors.surface.light.base,
      headerColor: colors.navy.DEFAULT,
      rowHoverBg: colors.primary.subtle,
      borderColor: colors.border.strong,
    },
    Modal: {
      headerBg: colors.surface.light.container,
      titleColor: colors.navy.DEFAULT,
      contentBg: colors.surface.light.container,
      footerBg: colors.surface.light.container,
      /*
       * **The close (X) button is the target.** antd 5.29.3 sizes it from an
       * internal token, not a component one — `modal/style/index.js`
       * `prepareToken`: `modalCloseBtnSize: token.controlHeight` — so the
       * documented-looking `modalCloseBtnSize` cannot be set here (it is
       * recomputed from `controlHeight` after component tokens merge).
       * The global theme only raises `controlHeightLG`, which left the X at
       * antd's 32px across the touch band. Overriding `controlHeight` in
       * this component's scope only reaches the Modal's own stylesheet
       * (its single read of `controlHeight` is that line); footer buttons
       * are styled by `Button`'s scope and are unaffected. The X stays
       * vertically centred on the title row because antd positions it at
       * `(modalHeaderHeight - modalCloseBtnSize) / 2`.
       * `pointerTheme` restores 32 from `lg` up.
       */
      controlHeight: layout.touchTarget,
    },
    /**
     * Drawers are a **light content surface**, not a navy one.
     *
     * These tokens used to carry the navy treatment, which was written for the
     * mobile *navigation* drawer — the only drawer that existed at bootstrap.
     * But the token applies to every antd `Drawer`, so once ADR-0008's real
     * detail and form drawers landed, every field label, collapse header and
     * helper line inside them rendered as ink on navy: illegible, and a clear
     * failure of the ADR-0007 contrast contract.
     *
     * The navigation drawer (`app/layout/MobileDrawer.tsx`) no longer opts out
     * of this: it was the one dark surface in an otherwise light mobile shell
     * and has been rewritten to use this same light theme, so there is now no
     * exception here to opt in locally.
     */
    Drawer: {
      colorBgElevated: colors.surface.light.container,
      colorText: colors.text.light.base,
      colorIcon: colors.text.light.muted,
      colorIconHover: colors.text.light.base,
    },
    Typography: {
      colorText: colors.text.light.base,
      colorTextHeading: colors.navy.DEFAULT,
      colorLink: colors.primary.DEFAULT,
      colorLinkHover: colors.primary.hover,
    },
    Tooltip: {
      colorBgSpotlight: colors.navy.DEFAULT,
      colorTextLightSolid: colors.text.light.onDark,
    },
    Empty: {
      colorTextDescription: colors.text.light.muted,
    },
    Result: {
      colorTextHeading: colors.navy.DEFAULT,
      colorTextDescription: colors.text.light.muted,
    },
  },
};

/**
 * Pointer-width control height: **32, antd's own default**, which is exactly
 * what HMS renders.
 *
 * Checked against their atoms rather than guessed — `Button.jsx` defaults to
 * `size="md"` → antd `"middle"`, `InputField.jsx` is a bare `Input` with no
 * size at all, and `hmsControls.css` sets no height or min-height anywhere.
 * So HMS is simply antd untouched, and matching it means our `size="large"`
 * call sites resolving to the same 32 at pointer widths.
 *
 * That makes `large` and `middle` identical above `lg`, which is the point:
 * `size="large"` in this codebase means "a control a gloved hand can hit",
 * and above `lg` there is no glove.
 */
const POINTER_CONTROL_HEIGHT = 32;

/**
 * `lightTheme` with the control height relaxed for genuinely mouse-driven
 * widths (manager's call, 2026-09-23 — "we have very large inputs and the
 * large buttons, we should use the same size as HMS").
 *
 * **The 44px floor still holds below `lg`,** which is where it means
 * something: `responsive-design.md` binds WCAG 2.5.5 there, and
 * `designTokens.ts` already settled this exact trade-off once — "matching HMS
 * does not extend to breaking our own accessibility floor". `lg`, not `md`,
 * is the boundary for the reason `ListPagination` gives at length: 768–1024px
 * is still tablets, so only from `lg` up is a pointer the safe assumption.
 *
 * So: HMS's density on a laptop, a compliant target on everything a finger
 * touches, and one token rather than 85 edited call sites.
 */
export const pointerTheme: ThemeConfig = {
  ...lightTheme,
  token: {
    ...lightTheme.token,
    controlHeightLG: POINTER_CONTROL_HEIGHT,
  },
  components: {
    ...lightTheme.components,
    /*
     * `lightTheme` pins the segmented control's own `controlHeightLG` (so each
     * segment clears 44 on touch widths), and a component token beats the
     * global one above — so without this, segmented controls would stay 48px
     * on a laptop. Pointer widths get the same 32 every other control does.
     */
    Segmented: {
      ...lightTheme.components?.Segmented,
      controlHeightLG: POINTER_CONTROL_HEIGHT,
    },
    /*
     * Same reason as `Segmented`: `lightTheme` raises the Modal's own
     * `controlHeight` so its close button clears 44 on touch widths, and the
     * spread above would carry that onto a laptop. Pointer widths keep
     * antd's 32px close button, as HMS renders it.
     */
    Modal: {
      ...lightTheme.components?.Modal,
      controlHeight: POINTER_CONTROL_HEIGHT,
    },
  },
};
